/**
 * LaborAvailabilityChartModel.js
 *
 * Builds chart data for Available Labor (per-cohort stacked) and Required Labor charts.
 * Uses event-point sweep line for available HC. ISO string comparison for cross-midnight safety.
 *
 * Also exposes:
 *   computeAvailableHCPerInterval     - time-weighted projection of shift cohorts
 *                                       (net of accepted VTOs) onto planning intervals.
 *   computeOpportunityHCPerInterval   - time-weighted projection of recommended
 *                                       VET / VTO HC onto planning intervals.
 *   classifyIntervalStatus            - per-interval shortage / surplus / null label,
 *                                       authoritative rule consumed by both the
 *                                       Recommendations table and the timeline.
 */

import { OPPORTUNITY_TYPE, VTO_OPPORTUNITY_TYPES } from '../../utils/laborOpportunityConstants';
import { DEFAULT_ATT_FRACTION } from '../../utils/constants';

// Cohort/VTO/opportunity ranges arrive as "YYYY-MM-DDTHH:MM" (station-local).
// Treat as UTC (append 'Z') so arithmetic is consistent with
// FlowPlannerModel.parseLocalISO, which feeds intervals[].startTime/endTime.
export const isoToEpochSec = (iso) => (iso ? Math.floor(new Date(iso + 'Z').getTime() / 1000) : 0);

const overlapSec = (aStart, aEnd, bStart, bEnd) => Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart));

/**
 * Project events onto planning intervals via time-weighted average:
 *
 *   value(interval) = Σ event.hc * overlap(event, interval) / duration(interval)
 *
 * Each event is { start, end, hc } in epoch seconds. This is the correct
 * counterpart to Required HC (HC needed throughout the interval) — it avoids
 * the over-count an "any overlap" sum produces when event boundaries clip an
 * interval, and the under-count a "starts-inside" check would produce.
 *
 * `clampNonNegative` floors each interval at 0; useful for available headcount,
 * where rounding or data inconsistency could otherwise yield a slight negative.
 */
function projectOntoIntervals(events, intervals, { clampNonNegative = false } = {}) {
  if (!events?.length || !intervals?.length) return intervals?.map(() => 0) || [];
  return intervals.map((iv) => {
    const duration = iv.endTime - iv.startTime;
    if (duration <= 0) return 0;
    const weighted = events.reduce(
      (sum, ev) => sum + (ev.hc * overlapSec(ev.start, ev.end, iv.startTime, iv.endTime)) / duration,
      0
    );
    return clampNonNegative ? Math.max(0, weighted) : weighted;
  });
}

const toEvent = (range, hc) => ({ start: isoToEpochSec(range.start), end: isoToEpochSec(range.end), hc });

/**
 * Time-weighted average available HC per planning interval, net of accepted VTOs.
 * For V3: applies intervalAttendanceOverrides per interval to the attendance-neutral base.
 */
export function computeAvailableHCPerInterval(laborAvailability, intervals) {
  if (!laborAvailability || !intervals?.length) return [];
  const overrides = laborAvailability.intervalAttendanceOverrides || null;
  const shiftEvents = (laborAvailability.shiftGroups || []).map((g) => toEvent(g, g.expectedHeadcount || 0));
  const vtoEvents = (laborAvailability.vtoAccepted || []).map((v) => toEvent(v, -(v.accepted || 0)));

  // If overrides present (V3), apply per-interval multiplication to shift events
  if (overrides) {
    return intervals.map((iv, idx) => {
      const dur = iv.endTime - iv.startTime;
      if (dur <= 0) return 0;
      const att = (Array.isArray(overrides) ? overrides[idx]?.attendancePercentage : undefined) ?? DEFAULT_ATT_FRACTION;
      const shiftHC = shiftEvents.reduce(
        (sum, ev) => sum + Math.round(ev.hc * att) * (overlapSec(ev.start, ev.end, iv.startTime, iv.endTime) / dur),
        0
      );
      const vtoHC = vtoEvents.reduce(
        (sum, ev) => sum + (ev.hc * overlapSec(ev.start, ev.end, iv.startTime, iv.endTime)) / dur,
        0
      );
      return Math.max(0, shiftHC + vtoHC);
    });
  }

  // V1/V2: use original logic (expectedHeadcount already STPA-baked)
  const events = [...shiftEvents, ...vtoEvents];
  return projectOntoIntervals(events, intervals, { clampNonNegative: true });
}

const VET_OPPORTUNITY_TYPES = new Set([OPPORTUNITY_TYPE.VET]);

/**
 * Time-weighted recommended VET / VTO HC per planning interval. DSVTO rolls up
 * into VTO via the shared VTO_OPPORTUNITY_TYPES set. Output values are
 * non-negative magnitudes, not signed.
 */
export function computeOpportunityHCPerInterval(laborOpportunities, intervals) {
  if (!intervals?.length) return { vet: [], vto: [] };
  const collect = (predicate) =>
    (laborOpportunities || [])
      .filter((o) => predicate.has(o.opportunityType))
      .map((o) => toEvent(o.applicability?.timeWindow || {}, o.headcount || 0));
  return {
    vet: projectOntoIntervals(collect(VET_OPPORTUNITY_TYPES), intervals),
    vto: projectOntoIntervals(collect(VTO_OPPORTUNITY_TYPES), intervals)
  };
}

// Threshold below which gap / recommendation HC is treated as noise. Used by
// classifyIntervalStatus so a 0.4-HC blip doesn't paint a slab red or green.
const STATUS_THRESHOLD_HC = 0.5;
const isAboveNoise = (value) => Math.abs(value || 0) >= STATUS_THRESHOLD_HC;

/**
 * Per-interval status used to color the Recommendations table column-header
 * indicator and the timeline track shading. Both views derive from this so
 * they cannot disagree.
 *
 *   shortage   gap < 0  OR a VET is recommended in the interval
 *              (a VET recommendation is the system saying "we're short" —
 *               that signal overrides a near-zero positive gap)
 *   surplus    gap > 0  AND no VET recommended  AND a VTO is recommended
 *              (must be both: an unused surplus with no VTO isn't actionable)
 *   null       balanced or below the noise threshold
 *
 * @param {number[]} requiredHC
 * @param {number[]} availableHC
 * @param {number[]} vetHC
 * @param {number[]} vtoHC
 * @returns {Array<'shortage' | 'surplus' | null>}
 */
export function classifyIntervalStatus(requiredHC, availableHC, vetHC, vtoHC) {
  if (!requiredHC?.length || !availableHC?.length) return [];
  return requiredHC.map((req, idx) => {
    const gap = (availableHC[idx] || 0) - (req || 0);
    const vetActive = isAboveNoise(vetHC?.[idx]);
    const vtoActive = isAboveNoise(vtoHC?.[idx]);
    if (gap < -STATUS_THRESHOLD_HC || vetActive) return 'shortage';
    if (gap > STATUS_THRESHOLD_HC && !vetActive && vtoActive) return 'surplus';
    return null;
  });
}

/** Infer a full ISO timestamp from an HH:MM label using date context from sorted shift ISOs. */
function inferISO(hhmm, sortedISOs) {
  if (!sortedISOs.length || !hhmm) return null;
  const firstDate = sortedISOs[0].substring(0, 10);
  const lastDate = sortedISOs[sortedISOs.length - 1].substring(0, 10);
  if (firstDate === lastDate) return firstDate + 'T' + hhmm;
  const firstHHMM = sortedISOs[0].substring(11, 16);
  return hhmm >= firstHHMM ? firstDate + 'T' + hhmm : lastDate + 'T' + hhmm;
}

function collectTimePoints(shiftGroups, vtoAccepted, intervals) {
  const pointMap = new Map();
  const events = [];

  (shiftGroups || []).forEach((g) => {
    if (g.start == null || g.end == null) return;
    pointMap.set(g.start, g.startLabel);
    pointMap.set(g.end, g.endLabel);
    events.push({ start: g.start, end: g.end, hc: g.expectedHeadcount || 0 });
  });

  (vtoAccepted || []).forEach((v) => {
    if (v.start == null || v.end == null) return;
    pointMap.set(v.start, v.startLabel);
    pointMap.set(v.end, v.endLabel);
    events.push({ start: v.start, end: v.end, hc: -(v.accepted || 0) });
  });

  const sortedISOs = [...pointMap.keys()].sort();
  (intervals || []).forEach((iv) => {
    [iv.startLabel, iv.endLabel].forEach((label) => {
      if (!label) return;
      const iso = inferISO(label, sortedISOs);
      if (iso && !pointMap.has(iso)) pointMap.set(iso, label);
    });
  });

  const sortedPoints = [...pointMap.keys()].sort();
  const categories = [];
  const seen = new Set();
  sortedPoints.forEach((iso) => {
    const label = pointMap.get(iso);
    if (label && !seen.has(label)) {
      seen.add(label);
      categories.push(label);
    }
  });

  return { pointMap, events, sortedPoints, categories };
}

/** Evaluate headcount at each time point by summing active events (sweep line). */
function sweepPerPoint(sortedPoints, events, pointMap) {
  return sortedPoints.map((pt) => {
    let hc = 0;
    events.forEach((ev) => {
      if (ev.start <= pt && ev.end > pt) hc += ev.hc;
    });
    return { x: pointMap.get(pt) || pt, y: Math.round(hc * 10) / 10 };
  });
}

/**
 * Build per-cohort and total available headcount series for the Available Labor chart.
 *
 * @param {{ shiftGroups: Array, vtoAccepted: Array }} laborAvailability
 * @param {Array<{ startLabel: string, endLabel: string }>} intervals
 * @returns {{ perCohortAvailable: Array, totalAvailable: Array, categories: string[] }}
 */
export function buildLaborAvailabilityChartData(laborAvailability, intervals) {
  const empty = { perCohortAvailable: [], totalAvailable: [], netAvailable: [], categories: [] };
  if (!laborAvailability) return empty;

  const { shiftGroups, vtoAccepted } = laborAvailability;
  const { pointMap, events, sortedPoints, categories } = collectTimePoints(shiftGroups, vtoAccepted, intervals);
  if (sortedPoints.length < 2) return empty;

  const perCohortAvailable = (shiftGroups || []).map((g) => {
    const ev = [{ start: g.start, end: g.end, hc: g.expectedHeadcount || 0 }];
    return { label: g.cohortLabel || `${g.startLabel}-${g.endLabel}`, data: sweepPerPoint(sortedPoints, ev, pointMap) };
  });

  // Total from shift group events only (matches the stacked cohorts)
  const shiftEvents = events.filter((e) => e.hc >= 0);
  const totalAvailable = sweepPerPoint(sortedPoints, shiftEvents, pointMap);

  // Net available = shift groups minus VTO (all events)
  const netAvailable = sweepPerPoint(sortedPoints, events, pointMap);

  return { perCohortAvailable, totalAvailable, netAvailable, categories };
}
